import routes from '~/routes';
import type { EvalUnifiedResult } from './types';

// ── URL Builder ──

const SOURCE_TO_EVAL_VERSION: Record<string, string> = {
  '2': 'eval_2',
  '3': 'eval_3',
};

/**
 * The API `source` literal for an eval-3 evaluation. Named so launchers (the eval-3 page's
 * Actions menu) and this module share one value instead of repeating the magic '3'.
 */
export const EVAL3_SOURCE = '3';

/**
 * The result page's default variance threshold — matches Query::PackageComparison's default, used
 * when a fresh (non-saved) pair is compared.
 */
export const DEFAULT_VARIANCE_THRESHOLD = 0.05;

/**
 * Builds the comparison results URL from a side pair and a variance threshold — the single owner
 * of the result-page `side_*` + `variance_threshold` query shape. A SAVED Package Compare reopens
 * through this directly (INS-12510); a freshly-picked pair goes via {@link buildCompareUrl}.
 * @param {object} sideA - The Origin side ({ evalVersion, evaluationId }).
 * @param {object} sideB - The Target side ({ evalVersion, evaluationId }).
 * @param {number} varianceThreshold - The threshold the compare ran with.
 * @return {string} Full URL to the comparison results page.
 */
export const buildSavedCompareResultUrl = (
  sideA: { evalVersion: string; evaluationId: number },
  sideB: { evalVersion: string; evaluationId: number },
  varianceThreshold: number
): string => {
  const params = new URLSearchParams({
    side_a_eval_version: sideA.evalVersion,
    side_a_evaluation_id: String(sideA.evaluationId),
    side_b_eval_version: sideB.evalVersion,
    side_b_evaluation_id: String(sideB.evaluationId),
    variance_threshold: String(varianceThreshold),
  });
  return `${routes.compareResultPath}?${params.toString()}`;
};

/**
 * Builds the comparison results URL from two freshly-selected evaluations. Maps each source
 * "2"/"3" to its eval_version and delegates the URL shape to {@link buildSavedCompareResultUrl},
 * so the two builders can't drift; a fresh compare runs at {@link DEFAULT_VARIANCE_THRESHOLD}.
 * @param {EvalUnifiedResult} evalA - First evaluation (baseline)
 * @param {EvalUnifiedResult} evalB - Second evaluation (compared against)
 * @return {string} Full URL to the comparison results page
 */
export const buildCompareUrl = (
  evalA: EvalUnifiedResult,
  evalB: EvalUnifiedResult
): string =>
  buildSavedCompareResultUrl(
    {
      evalVersion: SOURCE_TO_EVAL_VERSION[evalA.source] ?? evalA.source,
      evaluationId: Number(evalA.evalId),
    },
    {
      evalVersion: SOURCE_TO_EVAL_VERSION[evalB.source] ?? evalB.source,
      evaluationId: Number(evalB.evalId),
    },
    DEFAULT_VARIANCE_THRESHOLD
  );

// ── Origin Prefill (Compare wizard) ──

/**
 * Query-param names the wizard reads to pre-select the Origin (baseline).
 * Same `side_a_*` vocabulary the results URL uses (see buildCompareUrl) so the
 * whole Compare feature speaks one language for "which evaluation is side A".
 */
export const ORIGIN_EVAL_VERSION_PARAM = 'side_a_eval_version';
export const ORIGIN_EVALUATION_ID_PARAM = 'side_a_evaluation_id';

/** Inverse of SOURCE_TO_EVAL_VERSION — maps the URL's eval_version back to source. */
const EVAL_VERSION_TO_SOURCE: Record<string, string> = Object.fromEntries(
  Object.entries(SOURCE_TO_EVAL_VERSION).map(([source, version]) => [
    version,
    source,
  ])
);

/**
 * Resolves an eval_version query value ("eval_2"/"eval_3") to the API source
 * value ("2"/"3"). Unrecognized values pass through unchanged.
 * @param {string} evalVersion - The eval_version from the URL
 * @return {string} The matching source value
 */
export const evalVersionToSource = (evalVersion: string): string =>
  EVAL_VERSION_TO_SOURCE[evalVersion] ?? evalVersion;

/**
 * Builds the Compare wizard URL with the Origin (baseline) pre-filled. Used when
 * launching Compare from a specific evaluation (e.g. a package's Actions menu) so
 * the wizard opens with that eval already in the Origin slot; the user then picks
 * the Target.
 * @param {number} evaluationId - The origin evaluation id (eval_3 => EvaluationCalculation id)
 * @param {string} source - The origin API source value ("2" or "3")
 * @return {string} The wizard path with the side_a_* query params
 */
export const buildCompareWizardUrl = (
  evaluationId: number,
  source: string
): string => {
  const params = new URLSearchParams({
    [ORIGIN_EVAL_VERSION_PARAM]: SOURCE_TO_EVAL_VERSION[source] ?? source,
    [ORIGIN_EVALUATION_ID_PARAM]: String(evaluationId),
  });
  return `${routes.compareWizardPath}?${params.toString()}`;
};

// ── Source Display ──

const SOURCE_DISPLAY: Record<string, string> = {
  '2': '2.0',
  '3': '3.0',
};

/**
 * Formats the API source value for display.
 * @param {string} source - API source value ("2" or "3")
 * @return {string} Display label ("2.0" or "3.0")
 */
export const formatSource = (source: string): string =>
  SOURCE_DISPLAY[source] ?? source;
